import type { FC, FormEvent, ReactNode } from 'react'
import type { BrowseFilters } from '@/types'

import { useEffect, useId, useState } from 'react'
import { useRouter } from 'next/router'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildBrowseHref, DEFAULT_FILTERS } from '@/lib/browse'

interface SearchFormProps {
    defaultValue?: string
    baseFilters?: BrowseFilters
    size?: 'sm' | 'lg'
    placeholder?: string
    className?: string
    onSubmitted?: () => void
}

// Works without JavaScript as a plain GET form, and navigates client-side when it can.
export const SearchForm: FC<SearchFormProps> = ({
    defaultValue = '',
    baseFilters,
    size = 'sm',
    placeholder = 'Search wallpapers',
    className,
    onSubmitted
}): ReactNode => {
    const router = useRouter()
    const inputId = useId()
    const [value, setValue] = useState(defaultValue)

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        router.push(buildBrowseHref(baseFilters ?? DEFAULT_FILTERS, { q: value.trim() }))
        onSubmitted?.()
    }

    const large = size === 'lg'

    return (
        <form
            role='search'
            action='/wallpapers'
            method='get'
            onSubmit={onSubmit}
            className={cn('relative w-full', className)}
        >
            <label htmlFor={inputId} className='sr-only'>
                Search wallpapers
            </label>

            <Search
                aria-hidden='true'
                className={cn(
                    'pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground',
                    large ? 'left-5 size-5' : 'left-3 size-4'
                )}
            />

            <input
                id={inputId}
                type='search'
                name='q'
                value={value}
                onChange={event => setValue(event.target.value)}
                placeholder={placeholder}
                autoComplete='off'
                enterKeyHint='search'
                className={cn(
                    'w-full appearance-none border border-white/10 bg-white/[0.06] text-foreground outline-none transition placeholder:text-muted-foreground hover:border-white/20 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40 [&::-webkit-search-cancel-button]:hidden',
                    large
                        ? 'h-14 rounded-2xl pr-32 pl-13 text-base backdrop-blur-xl'
                        : 'h-10 rounded-xl pr-10 pl-9 text-sm'
                )}
            />

            {value && !large && (
                <button
                    type='button'
                    onClick={() => setValue('')}
                    className='absolute top-1/2 right-2 inline-flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition hover:bg-white/10 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/40 focus-visible:outline-none'
                >
                    <X aria-hidden='true' className='size-4' />
                    <span className='sr-only'>Clear search</span>
                </button>
            )}

            {large && (
                <button
                    type='submit'
                    className='absolute top-1/2 right-2 inline-flex h-10 -translate-y-1/2 cursor-pointer items-center gap-2 rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground transition hover:bg-primary/85 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                >
                    Search
                </button>
            )}
        </form>
    )
}
